import { useCallback } from 'react';

import { EditClient } from '@app/components/EditClient';
import type { ClientDataForm } from '@app/components/EditClient/types';
import { RecordDocuments } from '@app/components/RecordModal/components/ClientData/components/RecordDocuments';
import {
  setHasChanges,
  setIsInitialized,
  setIsLoading,
  setNextTab,
  setRecord,
  setRecordTab,
} from '@app/components/RecordModal/model/recordModalSlice';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import type { ContentPropsType } from '@app/components/RecordModal/types';
import { useRecordModalContext } from '@app/providers/RecordModalProvider';
import { useAppDispatch, useAppSelector } from '@app/store';

export const ClientData: ContentPropsType = ({ onCancel }) => {
  const dispatch = useAppDispatch();
  const { onConfirmRef } = useRecordModalContext();

  const {
    clientId,
    isOpen,
    record,
    hasChanges,
    isInitialized,
    isLoading,
    nextTab,
  } = useAppSelector(selectRecordModal);

  const setIsInitializedHandler = useCallback(
    (value: boolean) => {
      dispatch(setIsInitialized(value));
    },
    [dispatch]
  );

  const setIsLoadingHandler = useCallback(
    (value: boolean) => {
      dispatch(setIsLoading(value));
    },
    [dispatch]
  );

  const afterSave = useCallback(
    (values: ClientDataForm) => {
      const { NameFirst, NameLast, Email, IsPhoneVerified, Phone1 } = values;
      if (record) {
        dispatch(
          setRecord({
            ...record,
            NameFirst,
            NameLast,
            Email,
            IsPhoneVerified,
            Phone: Phone1.Phone,
            PhoneCountryId: Phone1.CountryId,
          })
        );
      }
      if (nextTab) {
        dispatch(setRecordTab(nextTab));
        dispatch(setNextTab(null));
        dispatch(setIsInitialized(false));
        dispatch(setHasChanges(false));
      }
      dispatch(setIsLoading(false));
    },
    [dispatch, record, nextTab]
  );

  const setOnConfirmHandler = useCallback(
    (confirmFunc: () => void) => {
      onConfirmRef.current = confirmFunc;
    },
    [onConfirmRef]
  );

  const setHasChangesHandler = useCallback(
    (value: boolean) => {
      dispatch(setHasChanges(value));
    },
    [dispatch]
  );

  return (
    <EditClient
      clientId={clientId}
      isInitialized={isInitialized}
      isOpen={isOpen}
      isLoading={isLoading}
      hasChanges={hasChanges}
      setHasChanges={setHasChangesHandler}
      onConfirm={onConfirmRef.current}
      afterSave={afterSave}
      setIsInitialized={setIsInitializedHandler}
      setIsLoading={setIsLoadingHandler}
      setOnConfirm={setOnConfirmHandler}
      onCancel={onCancel}
      titleExtra={<RecordDocuments recordId={record?.Id} />}
    />
  );
};
